import { AlertCircle, Edit3, List } from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import type { LoRScalarChoice } from '../types';

// Shared styling constants - more compact for review workflow
export const FIELD_STYLES = {
  selectTrigger: `
    transition-all duration-200 min-h-[40px] py-2
    hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/20
  `,
  selectTriggerError: `
    border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20
  `,
  input: `
    transition-all duration-200 h-[40px]
    hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500/20
  `,
  inputError: `
    border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20
  `,
} as const;

interface EnhancedSelectProps {
  value?: LoRScalarChoice | string;
  onChange: (value: LoRScalarChoice | string) => void;
  onBlur?: () => void;
  name?: string;
  choices: LoRScalarChoice[];
  placeholder?: string;
  manualPlaceholder?: string;
  fieldDisplayName?: string;
  error?: string;
  required?: boolean;
  renderTriggerContent?: (value: LoRScalarChoice | string) => React.ReactNode;
  renderChoiceContent?: (choice: LoRScalarChoice) => React.ReactNode;
  allowManualEntry?: boolean;
  emptyStateMessage?: string;
  emptyStateDescription?: string;
}

/**
 * Reusable mode toggle button
 */
const ModeToggle = ({
  isManual,
  hasChoices,
  onToggle,
}: {
  isManual: boolean;
  hasChoices: boolean;
  onToggle: () => void;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onToggle}
    className="h-5 px-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100"
  >
    {isManual ? (
      <>
        <List className="h-2.5 w-2.5 mr-1" />
        {hasChoices ? 'Select from options' : 'Back'}
      </>
    ) : (
      <>
        <Edit3 className="h-2.5 w-2.5 mr-1" />
        Enter manually
      </>
    )}
  </Button>
);

/**
 * Enhanced empty state component
 */
const EmptyState = ({
  message,
  description,
  onSwitchToManual,
  allowManualEntry,
}: {
  message: string;
  description: string;
  onSwitchToManual: () => void;
  allowManualEntry: boolean;
}) => (
  <Alert className="bg-blue-50 border-blue-200 py-2">
    <AlertCircle className="h-4 w-4 text-blue-600" />
    <AlertDescription className="text-blue-700">
      <div className="space-y-1.5">
        <p className="font-medium text-xs">{message}</p>
        <p className="text-xs">{description}</p>
        {allowManualEntry && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onSwitchToManual}
            className="text-blue-700 border-blue-300 hover:bg-blue-100 h-6 px-2 text-xs"
          >
            <Edit3 className="h-2.5 w-2.5 mr-1" />
            Enter manually
          </Button>
        )}
      </div>
    </AlertDescription>
  </Alert>
);

/**
 * Default trigger content renderer with error handling
 */
const DefaultTriggerContent = (value: LoRScalarChoice | string) => {
  try {
    if (typeof value === 'string') {
      return <span className="font-medium text-gray-900 text-sm">{value}</span>;
    }

    if (value && typeof value === 'object' && 'value' in value) {
      return (
        <span className="font-medium text-gray-900 text-sm">{value.value}</span>
      );
    }

    return (
      <span className="font-medium text-gray-500 text-sm">
        Select option...
      </span>
    );
  } catch (err) {
    console.error('Error rendering trigger content:', err);
    return (
      <span className="font-medium text-gray-500 text-sm">
        Select option...
      </span>
    );
  }
};

/**
 * Default choice content renderer with error handling
 */
const DefaultChoiceContent = (choice: LoRScalarChoice) => {
  try {
    if (choice && typeof choice === 'object' && 'value' in choice) {
      return (
        <span className="font-medium text-gray-900 text-sm">
          {choice.value}
        </span>
      );
    }
    return (
      <span className="font-medium text-gray-500 text-sm">Invalid option</span>
    );
  } catch (err) {
    console.error('Error rendering choice content:', err);
    return (
      <span className="font-medium text-gray-500 text-sm">Invalid option</span>
    );
  }
};

/**
 * Enhanced select component with manual override capability
 */
export const EnhancedSelect = ({
  value,
  onChange,
  onBlur,
  name,
  choices = [],
  placeholder = 'Select an option...',
  manualPlaceholder,
  fieldDisplayName = 'value',
  error,
  required,
  renderTriggerContent = DefaultTriggerContent,
  renderChoiceContent = DefaultChoiceContent,
  allowManualEntry = true,
  emptyStateMessage = 'No options available',
  emptyStateDescription = 'Options will appear when relevant data is available.',
}: EnhancedSelectProps) => {
  // Ensure choices is always an array
  const safeChoices = Array.isArray(choices) ? choices : [];

  const [isManualMode, setIsManualMode] = useState(safeChoices.length === 0);

  const handleToggleMode = () => {
    setIsManualMode(!isManualMode);
  };

  const manualInputPlaceholder =
    manualPlaceholder || `Enter ${fieldDisplayName.toLowerCase()}...`;

  // Determine current value for different modes with error handling
  const getDisplayValue = () => {
    try {
      if (typeof value === 'string') return value;
      if (value && typeof value === 'object' && 'value' in value)
        return value.value;
      return '';
    } catch (err) {
      console.error('Error getting display value:', err);
      return '';
    }
  };

  const getSelectValue = () => {
    try {
      if (typeof value === 'string') return value;
      if (value && typeof value === 'object' && 'value' in value)
        return value.value;
      return '';
    } catch (err) {
      console.error('Error getting select value:', err);
      return '';
    }
  };

  // Safe render trigger content with error boundary
  const renderTriggerContentSafely = (val: LoRScalarChoice | string) => {
    try {
      return renderTriggerContent(val);
    } catch (err) {
      console.error('Error in renderTriggerContent:', err);
      return DefaultTriggerContent(val);
    }
  };

  // Safe render choice content with error boundary
  const renderChoiceContentSafely = (choice: LoRScalarChoice) => {
    try {
      return renderChoiceContent(choice);
    } catch (err) {
      console.error('Error in renderChoiceContent:', err);
      return DefaultChoiceContent(choice);
    }
  };

  return (
    <div>
      {/* Main input/select area */}
      <div className="relative">
        {/* Manual input mode */}
        {isManualMode && (
          <Input
            value={getDisplayValue()}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            name={name}
            placeholder={manualInputPlaceholder}
            className={`${FIELD_STYLES.input} ${error ? FIELD_STYLES.inputError : ''}`}
          />
        )}

        {/* Empty state */}
        {!isManualMode && safeChoices.length === 0 && (
          <EmptyState
            message={emptyStateMessage}
            description={emptyStateDescription}
            onSwitchToManual={handleToggleMode}
            allowManualEntry={allowManualEntry}
          />
        )}

        {/* Select mode */}
        {!isManualMode && safeChoices.length > 0 && (
          <Select
            value={getSelectValue()}
            onValueChange={(selectedValue) => {
              try {
                const selectedChoice = safeChoices.find(
                  (choice) =>
                    choice &&
                    typeof choice === 'object' &&
                    choice.value === selectedValue,
                );
                onChange(selectedChoice || selectedValue);
              } catch (err) {
                console.error('Error in onValueChange:', err);
                onChange(selectedValue);
              }
            }}
          >
            <SelectTrigger
              className={`${FIELD_STYLES.selectTrigger} ${error ? FIELD_STYLES.selectTriggerError : ''}`}
            >
              <SelectValue placeholder={placeholder}>
                {value ? renderTriggerContentSafely(value) : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {safeChoices
                .filter(
                  (choice) =>
                    choice &&
                    typeof choice === 'object' &&
                    'value' in choice &&
                    choice.value !== '', // Filter out empty string values
                )
                .map((choice, index) => (
                  <SelectItem
                    key={`${choice.value}-${index}`}
                    value={choice.value}
                    className="cursor-pointer py-2"
                  >
                    {renderChoiceContentSafely(choice)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Error display and help text */}
      <div className="space-y-1 mt-1.5">
        {error && (
          <p className="text-xs text-red-600 font-medium flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" />
            {error}
          </p>
        )}

        {!error &&
          ((safeChoices.length > 0 && !isManualMode) ||
            isManualMode ||
            (allowManualEntry && (safeChoices.length > 0 || isManualMode))) && (
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-gray-500">
                {!isManualMode && safeChoices.length > 0 && (
                  <span>
                    {safeChoices.length} option
                    {safeChoices.length !== 1 ? 's' : ''} available
                  </span>
                )}
                {isManualMode && (
                  <span>Enter custom {fieldDisplayName.toLowerCase()}</span>
                )}
              </div>
              {allowManualEntry && (safeChoices.length > 0 || isManualMode) && (
                <ModeToggle
                  isManual={isManualMode}
                  hasChoices={safeChoices.length > 0}
                  onToggle={handleToggleMode}
                />
              )}
            </div>
          )}
      </div>
    </div>
  );
};
